import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import StepStage, { type StepAnnotation } from './StepStage';
import { STEP_VISUALS } from './stepVisuals';

export type ScrollyStep = {
  title: string;
  body: string;
  points: string[];
  linkLabel: string;
  linkHref: string;
  imageAlt: string;
};

/**
 * Desktop scroll story for the How it works steps: the step copy scrolls on the left while a
 * sticky stage on the right follows the step in the middle of the viewport. Content and anchors
 * (`#step-N`) are the same as the static layout.
 */
export default function StepsScrolly({
  steps,
  annotations,
  stepLabel,
}: {
  steps: ScrollyStep[];
  annotations: StepAnnotation[];
  stepLabel: (n: number) => string;
}) {
  const [active, setActive] = useState(0);
  const blockRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    let frame = 0;
    // The step whose block covers the vertical centre of the viewport is active.
    const update = () => {
      frame = 0;
      const mid = window.innerHeight / 2;
      let next = 0;
      blockRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= mid) next = i;
      });
      setActive(next);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [steps.length]);

  const n = steps.length;

  return (
    <div className="grid gap-16 lg:grid-cols-12">
      <ol className="m-0 list-none p-0 lg:col-span-5">
        {steps.map((step, i) => {
          const visual = STEP_VISUALS[i];
          const isActive = i === active;
          return (
            <li
              key={step.title}
              id={`step-${i + 1}`}
              ref={(el) => {
                blockRefs.current[i] = el;
              }}
              className="flex min-h-[75vh] scroll-mt-28 items-center"
            >
              <article className={`relative ps-8 transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-35'}`}>
                {/* Progress rail */}
                <span className="absolute start-0 top-1 h-full w-0.5 rounded-full bg-gray-200" aria-hidden />
                <span
                  className={`absolute start-0 top-1 w-0.5 rounded-full bg-blue-600 transition-all duration-500 ${isActive ? 'h-full' : 'h-0'}`}
                  aria-hidden
                />
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-500 ${
                      isActive ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    <i className={`${visual?.icon ?? 'ri-checkbox-circle-line'} text-xl`} aria-hidden />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">{stepLabel(i + 1)}</span>
                </div>
                <h3 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">{step.title}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-gray-600 sm:text-base">{step.body}</p>
                <ul className="m-0 mt-5 list-none space-y-2 p-0">
                  {step.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line mt-0.5 text-base text-emerald-600" aria-hidden />
                      {point}
                    </li>
                  ))}
                </ul>
                <Link to={step.linkHref} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600">
                  {step.linkLabel}
                  <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                </Link>
              </article>
            </li>
          );
        })}
      </ol>

      <div className="lg:col-span-7">
        <div className="sticky top-28 flex h-[calc(100vh-8rem)] flex-col justify-center">
          <StepStage
            active={active}
            count={n}
            alts={steps.map((s) => s.imageAlt)}
            annotations={annotations}
            className="mx-auto max-h-[60vh] max-w-[calc(60vh*1.6)]"
          />
          <div className="mt-10 flex items-center justify-center gap-2" aria-hidden>
            {steps.map((s, i) => (
              <span
                key={s.title}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === active ? 'w-8 bg-blue-600' : i < active ? 'w-1.5 bg-blue-300' : 'w-1.5 bg-gray-300'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
